import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import App from './App';
import ProtectedRoute from './Components/ProtectedRoute/ProtectedRoute';
import Home from './Pages/Home/Home';
import PagesApp from './PagesApp';
import { useUserContext } from './core/context/user.context';
import { toBool } from './core/helpers';
import useTracking from './core/hooks/useTracking';

// Lazy loaded components
const UserApp = lazy(() => import('./UserApp'));
const Apply = lazy(() => import('./Pages/Apply/Apply'));
const Register = lazy(() => import('./Pages/Register/Register'));
const ForgotPassword = lazy(() => import('./Pages/ForgotPassword/ForgotPassword'));
const Legal = lazy(() => import('./Pages/Legal/Legal'));
const EmailConfirm = lazy(() => import('./Components/User/EmailConfirm/EmailConfirm'));
const NewEmail = lazy(() => import('./Components/User/NewEmail/NewEmail'));
const Checkout = lazy(() => import('./Pages/Checkout/Checkout'));
const Info = lazy(() => import('./Pages/Info/Info'));
const Page = lazy(() => import('./Pages/Page/Page'));
const PageNotFound = lazy(() => import('./Pages/PageNotFound/PageNotFound'));
const ResetPassword = lazy(() => import('./Pages/ResetPassword/ResetPassword'));
const Subscription = lazy(() => import('./Pages/Subscription/Subscription'));
const Success = lazy(() => import('./Pages/Success/Success'));
const PlaygroundApp = lazy(() => import('./PlaygroundApp'));

function AppRoutes() {
  const { user } = useUserContext();
  const { trackUser } = useTracking();

  useEffect(() => {
    if (!user?.token) return;
    trackUser();
  }, [user?.token, trackUser]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<App />}>
          <Route element={<UserApp />}>
            <Route path="login" element={<Navigate to="/" replace />} />
            {toBool(process.env.REACT_APP_HIDE_REGISTRATION) ? null : (
              <Route path="apply" element={<Apply />} />
            )}
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="resetpassword" element={<ResetPassword />} />
            <Route path="success" element={<Success />} />
            <Route path="email-confirm/:hash" element={<EmailConfirm />} />
            <Route path="new-email" element={<NewEmail />} />
            <Route path="info" element={<Info />} />
          </Route>

          <Route element={<ProtectedRoute user={user} />}>
            {/* Disabled */}
            {/* <Route path="user" element={<PlaygroundApp />}>
            <Route index element={<UserOverview />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="profile" element={<Profile />} />
          </Route> */}
            <Route element={<PlaygroundApp />}>
              <Route path="subscription/:id" element={<Subscription />} />
              <Route path=":id/checkout" element={<Checkout />} />
            </Route>
          </Route>

          <Route element={<PagesApp />}>
            <Route index element={<Home />} />
            <Route path="terms-of-use" element={<Legal type="termsAndConditions" />} />
            <Route path="privacy-policy" element={<Legal type="privacyPolicy" />} />
            <Route path=":slug" element={<Page />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
